import React, { useState, useEffect } from 'react';
import { Database, Trash, Trash2, AlertCircle, CheckCircle, Loader, Lock, RefreshCw } from 'lucide-react';
import { apiCall } from '../api';
import { useMountEffect } from '../hooks/useMountEffect';
import './AdminPages.css';

const DatabaseManagement = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [selectedCollections, setSelectedCollections] = useState(new Set());
  const [currentUserRole, setCurrentUserRole] = useState(null);

  const isSuperAdmin = currentUserRole === 'superadmin';

  // Protected collections that should not be deleted or shown
  const PROTECTED_COLLECTIONS = new Set(['users', 'settings', 'sessions', 'roles', 'amcs', 'json_schemas', 'pdf_templates']);

  useMountEffect(() => {
    // Get current user role and fetch collections only if authenticated
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      setError('Please login to access this page');
      setLoading(false);
      return;
    }

    const user = JSON.parse(userData);
    setCurrentUserRole(user.role);
    fetchCollections();
  });

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/admin/collections');
      // Filter out protected collections
      const filtered = (Array.isArray(data) ? data : []).filter(c => !PROTECTED_COLLECTIONS.has(c.name));
      setCollections(filtered);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleCollection = (collectionName) => {
    const newSelected = new Set(selectedCollections);
    if (newSelected.has(collectionName)) {
      newSelected.delete(collectionName);
    } else {
      newSelected.add(collectionName);
    }
    setSelectedCollections(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedCollections.size === collections.length) {
      setSelectedCollections(new Set());
    } else {
      setSelectedCollections(new Set(collections.map(c => c.name)));
    }
  };

  const handleDeleteCollection = async (collectionName) => {
    try {
      setDeleting(collectionName);
      await apiCall(`/api/admin/collections/delete`, {
        method: 'POST',
        body: JSON.stringify({ collections: [collectionName] })
      });
      setSuccess(`✓ Collection '${collectionName}' deleted successfully`);
      setCollections(collections.filter(c => c.name !== collectionName));
      setConfirmDelete(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const handleClearCollection = async (collectionName) => {
    try {
      setDeleting(collectionName);
      await apiCall(`/api/admin/collections/clear`, {
        method: 'POST',
        body: JSON.stringify({ collections: [collectionName] })
      });
      setSuccess(`✓ Collection '${collectionName}' cleared successfully`);
      fetchCollections();
      setConfirmDelete(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const handleBulkClear = async () => {
    if (selectedCollections.size === 0) {
      setError('Please select at least one collection');
      return;
    }

    try {
      setDeleting('bulk');
      const response = await fetch('/api/admin/collections/clear', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ collections: Array.from(selectedCollections) })
      });

      if (response.ok) {
        setSuccess(`✓ Cleared ${selectedCollections.size} collection(s) successfully`);
        setSelectedCollections(new Set());
        setConfirmDelete(null);
        fetchCollections();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(`Failed to clear collections: ${response.statusText}`);
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1><Database size={28} /> Database Management</h1>
          <p>Manage MongoDB collections and data</p>
        </div>
        <div className="form-section" style={{ textAlign: 'center', padding: '40px' }}>
          <Loader size={32} className="spinner" style={{ margin: '0 auto', color: '#3b82f6' }} />
          <p style={{ marginTop: '16px', color: '#64748b' }}>Loading collections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1><Database size={28} /> Database Management</h1>
          <p>Manage MongoDB collections and data</p>
        </div>
        {!isSuperAdmin && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: '#f3f4f6',
            borderRadius: '8px',
            color: '#6b7280',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            🔒 Read Only
          </div>
        )}
      </div>

      {error && (
        <div className="status-message error">
          <AlertCircle size={16} /> {error}
          <button onClick={() => setError('')} className="close-btn">✕</button>
        </div>
      )}

      {success && (
        <div className="status-message success">
          <CheckCircle size={16} /> {success}
          <button onClick={() => setSuccess('')} className="close-btn">✕</button>
        </div>
      )}

      <div className="form-section">
        <div className="section-header">
          <h3>
            <Database size={20} /> Collections Overview
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            {selectedCollections.size > 0 && isSuperAdmin && (
              <button
                className="btn btn-warning"
                onClick={() => setConfirmDelete({ action: 'bulk-clear', collections: Array.from(selectedCollections) })}
                disabled={deleting === 'bulk'}
              >
                <Trash size={16} />
                Clear {selectedCollections.size} Selected
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={fetchCollections}>
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>
        </div>

        {collections.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            <Database size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
            <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>No Collections Found</p>
            <p style={{ fontSize: '14px' }}>All collections are either protected or empty</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '16px', padding: '12px', background: '#dbeafe', borderRadius: '8px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <Lock size={16} style={{ color: '#0369a1', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '12px', color: '#0369a1' }}>
                <strong>Protected Collections:</strong> users, settings, sessions, roles, amcs, json_schemas, pdf_templates cannot be deleted or accessed to protect application integrity.
              </div>
            </div>
            <div className="collections-table">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selectedCollections.size === collections.length && collections.length > 0}
                        onChange={toggleSelectAll}
                        disabled={!isSuperAdmin}
                        title="Select all collections"
                      />
                    </th>
                    <th>Collection Name</th>
                    <th>Documents</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {collections.map(collection => (
                    <tr key={collection.name}>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedCollections.has(collection.name)}
                          onChange={() => toggleCollection(collection.name)}
                          disabled={!isSuperAdmin}
                        />
                      </td>
                      <td>
                        <strong>{collection.name}</strong>
                      </td>
                      <td>
                        <span className="badge-info">{collection.count} documents</span>
                      </td>
                      <td>
                        {isSuperAdmin ? (
                          <button
                            className="action-btn clear-btn"
                            onClick={() => setConfirmDelete({ action: 'clear', collection: collection.name })}
                            disabled={deleting === collection.name}
                            title="Clear all documents from collection"
                          >
                            {deleting === collection.name ? (
                              <>
                                <Loader size={14} className="spinner" />
                              </>
                            ) : (
                              <>
                                <Trash2 size={14} />
                                <span>Clear Data</span>
                              </>
                            )}
                          </button>
                        ) : (
                          <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Read Only</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>
              {confirmDelete.action === 'delete' ? '⚠️ Delete Collection' : confirmDelete.action === 'bulk-clear' ? '⚠️ Clear Multiple Collections' : '⚠️ Clear Collection'}
            </h3>
            {confirmDelete.action === 'bulk-clear' ? (
              <div>
                <p>This will delete all documents in {confirmDelete.collections.length} collection(s). The collection structure will be kept. This action cannot be undone.</p>
                <div style={{ maxHeight: '250px', overflowY: 'auto', background: '#f9fafb', padding: '1rem', borderRadius: '6px', marginBottom: '1.5rem' }}>
                  <ol style={{ margin: 0, paddingLeft: '1.5rem', fontSize: '0.9rem', color: '#374151' }}>
                    {confirmDelete.collections.map((collection, idx) => (
                      <li key={idx} style={{ marginBottom: '0.5rem' }}>
                        {collection}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            ) : (
              <p>
                {confirmDelete.action === 'delete'
                  ? `This will permanently delete the collection '${confirmDelete.collection}' and all its data. This action cannot be undone.`
                  : `This will delete all documents in '${confirmDelete.collection}' but keep the collection structure. This action cannot be undone.`}
              </p>
            )}
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                className={`btn ${confirmDelete.action === 'delete' ? 'btn-danger' : 'btn-warning'}`}
                onClick={() => {
                  if (confirmDelete.action === 'delete') {
                    handleDeleteCollection(confirmDelete.collection);
                  } else if (confirmDelete.action === 'bulk-clear') {
                    handleBulkClear();
                  } else {
                    handleClearCollection(confirmDelete.collection);
                  }
                }}
                disabled={deleting === confirmDelete.collection || deleting === 'bulk'}
              >
                {confirmDelete.action === 'delete' ? 'Delete Collection' : 'Clear Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseManagement;
